import FocusManager from '../utils/focus-manager';
import ScrollManager from '../utils/scroll-manager';

export default function initHeaderNavigation() {
    new Header(document.getElementById('main-header'));
}

/**
 * @param {HTMLElement}       $el                                  HTML Element header
 * @param {HTMLElement}       $body                                HTML Body
 * @param {HTMLButtonElement} $menuToggler                         Toggler du menu
 * @param {HTMLElement}       $mainHeaderMenu                      Le menu principal
 * @param {HTMLElement}       $mainHeaderSubmenuBtnReturn          Le menu retour pour mobile
 * @param {integer}           mobileBreakpoint                     Breakpoint mobile
 * @param {string}            initializedDevice                    Nom du device initialisé (mobile | desktop)
 * @param {Element[]}         $openedPanels                        Liste des panneaux ouverts
 * 
 */
class Header {
    $el : HTMLElement | null;
    $body : HTMLElement | null;
    $menuToggler : HTMLButtonElement | null | undefined;
    $mainHeaderNav : HTMLElement | null | undefined;
    $mainHeaderMenu : HTMLElement | null | undefined;
    $mainHeaderSubmenuBtnReturn : NodeListOf<HTMLElement> | null | undefined;
    $mainHeaderSubmenuBtnReturnLvl2 : NodeListOf<HTMLElement> | null | undefined;
    mobileBreakpoint : number;
    initializedDevice : string | null;
    $openedPanels : Element[];
    $submenuTabs : NodeListOf<HTMLElement> | null | undefined;
    $submenuTabsContent : NodeListOf<HTMLElement> | null | undefined;
    $menuItemSubmenuOpeners : NodeListOf<HTMLElement> | null | undefined;
    $wrapperSummary : HTMLElement | null;
    private lastScrollY : number = 0;
    private boundMobileClickMenuTogglerHandler: (() => void) | null = null;
    private boundHandleSubmenuOpenerClick: ((event: Event) => void);
    private boundHandleDocumentClick: ((event: MouseEvent) => void);
    private boundHandleTabClick: ((event: Event) => void);
    private boundHandleTabClickMobile: ((event: Event) => void);
    private lastFocusedElement: HTMLElement | null = null;

    constructor($el : HTMLElement | null) {
        this.$el = $el;
        this.$body = document.querySelector('body');
        this.$menuToggler = $el?.querySelector('.js-main-header-burger-button');
        this.$mainHeaderNav = $el?.querySelector('.js-main-header-navigation');
        this.$mainHeaderMenu = $el?.querySelector('.js-main-header-menu');
        this.$mainHeaderSubmenuBtnReturn = $el?.querySelectorAll('.js-btn-submenu-return');
        this.$mainHeaderSubmenuBtnReturnLvl2 = $el?.querySelectorAll('.js-btn-submenu-return-lvl2');
        this.mobileBreakpoint = parseInt(getComputedStyle(document.documentElement).getPropertyValue('--breakpoint-main-header')) + 1;
        this.initializedDevice = null;
        this.$openedPanels = [];
        this.$submenuTabs = $el?.querySelectorAll('.menu-item-submenu-tab-opener');
        this.$submenuTabsContent = $el?.querySelectorAll('.menu-item-submenu-tabs-content-item');
        this.$menuItemSubmenuOpeners = $el?.querySelectorAll('.menu-item-submenu-opener');
        this.$wrapperSummary = document.querySelector('.js-wrapper-summary');
        this.boundHandleSubmenuOpenerClick = this.handleSubmenuOpenerClick.bind(this);
        this.boundHandleDocumentClick = this.handleDocumentClick.bind(this);
        this.boundHandleTabClick = this.handleTabClick.bind(this);
        this.boundHandleTabClickMobile = this.handleTabClickMobile.bind(this);

        this.init();
        window.addEventListener('resize', this.init.bind(this));
    }

    /**
     * Initialise le header dans le bon device en fonction
     * de la taille de l'écran (mobile ou desktop)
     *
     * @return {void}
     */
    init() {
        if (window.innerWidth < this.mobileBreakpoint && this.initializedDevice !== 'mobile') {
            this.initMobile();
        } else if (window.innerWidth >= this.mobileBreakpoint && this.initializedDevice !== 'desktop') {
            this.initDesktop();
        }

        if (this.$body) {
            if (!this.$body.classList.contains('page-template-contact')) {
                this.initStickyNavigation();
                this.handleSecondaryStickyBehavior();
                window.addEventListener('scroll', () => {
                    this.initStickyNavigation();
                    this.handleSecondaryStickyBehavior();
                });
            }
        }
    }

    /**
     * Initialise le header mobile
     *
     * @return {void}
     */
    initMobile() {
        this.initializedDevice = 'mobile';
        this.destroyDesktop();

        // Ouverture / Fermeture du menu au clique sur le toggler du menu
        if (this.$menuToggler) {
            this.boundMobileClickMenuTogglerHandler = this.mobileClickMenuTogglerHandler.bind(this);
            this.$menuToggler.addEventListener('click', this.boundMobileClickMenuTogglerHandler);
        }

        // Fermeture du menu à l'appuie sur la touche Escape
        document.addEventListener('keydown', this.mobileKeyDownDocumentHandler.bind(this));

        // Initialiser les boutons de retour
        this.$mainHeaderSubmenuBtnReturn?.forEach($link => {
            $link.addEventListener('click', (event) => this.handleMobileReturnClick(event));
        });

        // Initialiser les boutons de retour de niveau 2
        this.$mainHeaderSubmenuBtnReturnLvl2?.forEach($link => {
            $link.addEventListener('click', (event) => this.handleMobileReturnLvl2Click(event));
        });

        // Initialiser les événements des sous-menus
        this.$menuItemSubmenuOpeners?.forEach(($opener) => {
            $opener.addEventListener('click', this.boundHandleSubmenuOpenerClick);
        });

        // Retirer les tabindex en mobile
        this.$submenuTabs?.forEach(($tab) => {
            $tab.addEventListener('click', this.boundHandleTabClickMobile);
            $tab.removeAttribute('tabindex');
        });
    }

    /**
     * Fonction à éxécuter au clique sur le toggler du menu
     *
     * @return {void}
     */
    mobileClickMenuTogglerHandler() {
        if (this.$el?.classList.contains('open')) {
            this.closeMobileMenu();
        } else {
            this.openMobileMenu();
        }
    }

    /**
     * Fonction à éxécuter à l'appuie sur une touche du clavier
     *
     * @return {void}
     */
    mobileKeyDownDocumentHandler(evt: KeyboardEvent) {
        if (evt.key === 'Escape' && this.$el?.classList.contains('open')) {
            this.closeMobileMenu(true);
            // On enlève le trap focus
            FocusManager.untrapFocus();
            // On remet le focus sur l'élément qui a ouvert le menu
            if (this.lastFocusedElement) {
                this.lastFocusedElement.focus();
            }
        }
    }

    /**
     * Fonction à éxécuter au clique sur un lien du menu contenant des enfants
     *
     * @return {void}
     */
    mobileClickMainHeaderNavLinkWithChildrenHandler(evt: Event) {
        evt.preventDefault();
        const $item = (evt.target as Element)?.closest('.menu-item');
        
        if ($item) {
            // Si l'élément est déjà ouvert, on le ferme
            if ($item.classList.contains('open')) {
                $item.classList.remove('open');
                const index = this.$openedPanels.indexOf($item);
                if (index > -1) {
                    this.$openedPanels.splice(index, 1);
                }
            } else {
                // Sinon on l'ouvre
                $item.classList.add('open');
                this.$openedPanels.push($item);
            }
            
            // On met à jour l'état du menu principal
            if (this.$openedPanels.length > 0) {
                this.$mainHeaderNav?.classList.add('submenu-is-open');
            } else {
                this.$mainHeaderNav?.classList.remove('submenu-is-open');
            }
        }
    }

    /**
     * Initialise le header desktop
     *
     * @return {void}
     */
    initDesktop() {
        this.destroyMobile();
        this.initializedDevice = 'desktop';
        
        // Initialiser les événements des sous-menus
        this.$menuItemSubmenuOpeners?.forEach(($opener) => {
            $opener.addEventListener('click', this.boundHandleSubmenuOpenerClick);
        });

        // Initialiser les événements des onglets
        this.$submenuTabs?.forEach(($tab) => {
            $tab.addEventListener('click', this.boundHandleTabClick);
            $tab.addEventListener('keydown', (event) => this.handleTabKeyDown(event));
            
            // Gérer les tabindex en desktop
            if ($tab.classList.contains('active')) {
                $tab.setAttribute('tabindex', '0');
            } else {
                $tab.setAttribute('tabindex', '-1');
            }
        });

        // Ajouter l'écouteur de clic pour fermer les sous-menus en desktop
        document.addEventListener('click', this.boundHandleDocumentClick);
        document.addEventListener('keydown', this.handleDocumentKeyDown.bind(this));
    }
    
    /**
     * Initialise les event clavier sur le menu desktop
     *
     * @return {void}
     */
    handleDocumentKeyDown(event: KeyboardEvent): void {
        const keyboardEvent = event as KeyboardEvent;
        if (keyboardEvent.key === 'Escape' || keyboardEvent.key === 'Esc' || keyboardEvent.keyCode === 27) {
            // Fermer tous les sous-menus
            this.$el?.querySelectorAll('.menu-item.open').forEach((item) => {
                item.classList.remove('open');
                item.querySelector('.menu-item-link')?.classList.remove('active');
            });
        }
    }

    /**
     * Gère le clic sur un onglet en desktop
     * 
     * @param {Event} event 
     * @return {void}
     */
    handleTabClick(event: Event) {
        const tab = event.currentTarget as HTMLElement;
        const tabId = tab.getAttribute('data-tab-menu-target');
        if (!tabId) return;
        // Trouver le sous-menu parent
        const submenu = tab.closest('.menu-item-submenu');
        if (!submenu) return;
        // Onglets et panels de CE sous-menu
        const tabs = submenu.querySelectorAll('.menu-item-submenu-tab-opener');
        const panels = submenu.querySelectorAll('.menu-item-submenu-tabs-content-item');
        // Désactive tous les onglets et contenus de ce sous-menu
        tabs.forEach(($tab) => {
            $tab.classList.remove('active');
            $tab.setAttribute('aria-expanded', 'false');
            $tab.setAttribute('tabindex', '-1');
        });
        panels.forEach(($content) => {
            $content.classList.remove('active');
        });
        // Active l'onglet cliqué et son contenu
        tab.classList.add('active');
        tab.setAttribute('aria-expanded', 'true');
        tab.setAttribute('tabindex', '0');
        const targetContent = submenu.querySelector(`[data-tab-menu="${tabId}"]`);
        targetContent?.classList.add('active');
        // Met le focus sur l'onglet actif
        tab.focus();
    }

    /**
     * Gère le clic sur un onglet en mobile
     * 
     * @param {Event} event 
     * @return {void}
     */
    handleTabClickMobile(event: Event) {
        const tab = event.currentTarget as HTMLElement;
        const tabId = tab.getAttribute('data-tab-menu-target');
        if (!tabId) return;
        // Trouver le sous-menu parent
        const submenu = tab.closest('.menu-item-submenu');
        if (!submenu) return;
        // Onglets et panels de CE sous-menu
        const tabs = submenu.querySelectorAll('.menu-item-submenu-tab-opener');
        const panels = submenu.querySelectorAll('.menu-item-submenu-tabs-content-item');
        // Désactive tous les onglets et contenus de ce sous-menu
        tabs.forEach(($tab) => {
            $tab.classList.remove('open');
            $tab.setAttribute('aria-expanded', 'false');
        });
        panels.forEach(($content) => {
            $content.classList.remove('open');
        });
        // Active l'onglet cliqué et son contenu
        tab.classList.add('open');
        tab.setAttribute('aria-expanded', 'true');
        const targetContent = submenu.querySelector(`[data-tab-menu="${tabId}"]`);
        targetContent?.classList.add('open');
        // On active le trap focus sur le contenu de l'onglet
        if (targetContent) {
            FocusManager.trapFocus([targetContent as HTMLElement]);
        }
        // Met le focus sur l'onglet actif
        tab.focus();
    }

    /**
     * Gère les événements clavier sur les onglets
     * 
     * @param {KeyboardEvent} event 
     * @return {void}
     */
    handleTabKeyDown(event: KeyboardEvent): void {
        const tab = event.currentTarget as HTMLElement;
        const tabs = Array.from(this.$submenuTabs || []);
        const currentIndex = tabs.indexOf(tab);
        let prevTab: HTMLElement | null | undefined;
        let nextTab: HTMLElement | null | undefined;
        let firstLink: HTMLElement | null | undefined;
        let activeTab: HTMLElement | null | undefined;
        let activeContent: HTMLElement | null | undefined;

        switch (event.key) {
            case 'ArrowLeft':
                event.preventDefault();
                prevTab = tabs[currentIndex - 1] || tabs[tabs.length - 1];
                if (prevTab) {
                    (prevTab as HTMLElement).focus();
                }
                break;

            case 'ArrowRight':
                event.preventDefault();
                nextTab = tabs[currentIndex + 1] || tabs[0];
                if (nextTab) {
                    (nextTab as HTMLElement).focus();
                }
                break;
            case 'Enter':
                event.preventDefault();
                // Activer le bouton cliqué
                this.handleTabClick(event);
                // Mettre le focus sur le premier lien du contenu
                activeTab = this.$el?.querySelector('.menu-item-submenu-tab-opener.active');
                if (activeTab) {
                    activeContent = this.$el?.querySelector(`[data-tab-menu="${activeTab.getAttribute('data-tab-menu-target')}"]`);
                    firstLink = activeContent?.querySelector('.menu-item-submenu-tab-link');
                    if (firstLink) {
                        (firstLink as HTMLElement).focus();
                    }
                }
                break;

            case ' ':
                event.preventDefault();
                this.handleTabClick(event);
                break;
        }
    }

    /**
     * Gère le clic sur le document pour fermer le sous menus
     * si on clique à l'extérieur
     * 
     * @param {MouseEvent} event 
     * @return {void}
     */
    handleDocumentClick(event: MouseEvent) {
        const target = event.target as HTMLElement;
        
        // Fermer les sous-menus lors d'un clic à l'extérieur
        if (!target.closest('.menu-item-with-submenu')) {
            this.$el?.querySelectorAll('.menu-item.open').forEach((item) => {
                item.classList.remove('open');
                item.querySelector('.menu-item-link')?.classList.remove('active');
            });
        }
    }

    /**
     * Détruit le header mobile
     *
     * @return {void}
     */
    destroyMobile() {
        // On ré-autorise le scroll dans la page
        ScrollManager.preventScroll(false);

        // On enlève la contrainte du focus
        FocusManager.untrapFocus();

        // On ferme le menu s'il était ouvert pour éviter les erreurs
        this.$el?.classList.remove('open');
        this.$body?.classList.remove('menu-is-open');

        // Suppression des events bind lors de l'initialisation du header mobile
        if (this.$menuToggler && this.boundMobileClickMenuTogglerHandler) {
            this.$menuToggler.removeEventListener('click', this.boundMobileClickMenuTogglerHandler);
            this.boundMobileClickMenuTogglerHandler = null;
        }

        // Supprimer les écouteurs des boutons de retour
        this.$mainHeaderSubmenuBtnReturn?.forEach($link => {
            $link.removeEventListener('click', (event) => this.handleMobileReturnClick(event));
        });

        // Supprimer les écouteurs des boutons de retour de niveau 2
        this.$mainHeaderSubmenuBtnReturnLvl2?.forEach($link => {
            $link.removeEventListener('click', (event) => this.handleMobileReturnLvl2Click(event));
        });

        // Supprimer les écouteurs des sous-menus
        this.$menuItemSubmenuOpeners?.forEach(($opener) => {
            $opener.removeEventListener('click', this.boundHandleSubmenuOpenerClick);
        });

        // Supprimer les écouteurs des onglets
        this.$submenuTabs?.forEach(($tab) => {
            $tab.removeEventListener('click', this.boundHandleTabClickMobile);
        });

        document.removeEventListener('keydown', this.mobileKeyDownDocumentHandler);
    }

    /**
     * Détruit le header desktop
     *
     * @return {void}
     */
    destroyDesktop() {
        // Supprimer les écouteurs globaux
        document.removeEventListener('keydown', this.handleDocumentKeyDown.bind(this));
        document.removeEventListener('click', this.boundHandleDocumentClick);

        // Supprimer les écouteurs des sous-menus
        this.$menuItemSubmenuOpeners?.forEach(($opener) => {
            $opener.removeEventListener('click', this.boundHandleSubmenuOpenerClick);
        });

        // Supprimer les écouteurs des onglets
        this.$submenuTabs?.forEach(($tab) => {
            $tab.removeEventListener('click', this.boundHandleTabClick);
            $tab.removeEventListener('keydown', (event) => this.handleTabKeyDown(event));
        });
    }

    /**
     * Initialise le header sticky
     *
     * @return {void}
     */
    initStickyNavigation() {
        if (window.pageYOffset > 20) {
            this.$el?.classList.add('main-header--sticky');
            this.$body?.classList.add('main-header--is-sticky');
        } else {
            this.$el?.classList.remove('main-header--sticky');
            this.$body?.classList.remove('main-header--is-sticky');
        }
    }

    /**
     * Ouvre le menu mobile
     *
     * @return {void}
     */
    openMobileMenu() {
        this.$el?.classList.add('open');
        this.$body?.classList.add('menu-is-open');

        // ScrollManager
        //     .scrollTo(0)
        //     .preventScroll(true);

        ScrollManager.preventScroll(true);
        
        FocusManager.trapFocus(
            [this.$el]
        );
    }

    /**
     * Ferme le menu mobile
     *
     * @param {boolean} onlyLastPanel Seulement le dernier panneau ouvert
     * @return {void}
     */
    closeMobileMenu(onlyLastPanel: boolean = false): void {
        if (onlyLastPanel && this.$openedPanels.length > 0) {
            this.closeMobileSubmenu();
        } else {
            this.$el?.classList.remove('open');
            this.$mainHeaderNav?.classList.remove('submenu-is-open');
            this.$body?.classList.remove('menu-is-open');

            this.$openedPanels.forEach($panel => {
                $panel.classList.remove('open');
            });
            this.$openedPanels = [];

            // On remet le scroll de la page
            ScrollManager.preventScroll(false);

            // On enlève la contrainte du focus
            FocusManager.untrapFocus();

            // On remet le focus sur le bouton burger
            if (this.$menuToggler) {
                this.$menuToggler.focus();
            }
        }
    }

    /**
     * Ouvre un sous-menu mobile
     *
     * @param {Element} $item
     * @param {string}      $itemLabel
     * @return {void}
     */
    openMobileSubmenu($item : Element) {
        $item?.classList.add('open');

        this.$openedPanels?.push($item);

        this.$mainHeaderNav?.classList.add('submenu-is-open');
    }

    /**
     * Ferme le dernier sous-menu mobile ouvert
     *
     * @return {void}
     */
    closeMobileSubmenu() {
        const $item = this.$openedPanels?.pop();
        $item?.classList.remove('open');
        this.$mainHeaderNav?.classList.remove('submenu-is-open');
    }

    private handleSubmenuOpenerClick(event: Event): void {
        event.preventDefault();
        event.stopPropagation();
        
        const opener = event.currentTarget as HTMLElement;
        const menuItem = opener.closest('.menu-item');
        
        if (!menuItem) return;

        // En mode mobile, on ne ferme pas les autres sous-menus
        if (window.innerWidth >= this.mobileBreakpoint) {
            // Fermer tous les autres sous-menus
            this.$el?.querySelectorAll('.menu-item.open').forEach((item) => {
                if (item !== menuItem) {
                    item.classList.remove('open');
                    item.querySelector('.menu-item-link')?.classList.remove('active');
                }
            });
        }

        // Basculer l'état du sous-menu actuel
        const wasOpen = menuItem.classList.contains('open');
        menuItem.classList.toggle('open');
        menuItem.querySelector('.menu-item-link')?.classList.toggle('active');

        // Gérer les openedPanels en mobile
        if (window.innerWidth < this.mobileBreakpoint) {
            if (wasOpen) {
                // Si le menu était ouvert, on le retire des openedPanels
                const index = this.$openedPanels.indexOf(menuItem);
                if (index > -1) {
                    this.$openedPanels.splice(index, 1);
                }
                // On enlève le trap focus
                FocusManager.untrapFocus();
                // On remet le focus sur l'élément qui a ouvert le menu
                if (this.lastFocusedElement) {
                    this.lastFocusedElement.focus();
                }
            } else {
                // Si le menu était fermé, on l'ajoute aux openedPanels
                this.$openedPanels.push(menuItem);
                // On stocke l'élément qui a ouvert le menu
                this.lastFocusedElement = opener;
                // On active le trap focus sur le sous-menu
                const submenu = menuItem.querySelector('.menu-item-submenu');
                if (submenu) {
                    FocusManager.trapFocus([submenu as HTMLElement]);
                }
            }

            // Mettre à jour l'état du menu principal
            if (this.$openedPanels.length > 0) {
                this.$mainHeaderNav?.classList.add('submenu-is-open');
            } else {
                this.$mainHeaderNav?.classList.remove('submenu-is-open');
            }
        }
    }

    /**
     * Gère le clic sur les boutons de retour en mobile
     * 
     * @param {Event} event 
     * @return {void}
     */
    private handleMobileReturnClick(event: Event): void {
        event.preventDefault();
        
        // Fermer tous les sous-menus ouverts
        this.$openedPanels.forEach(panel => {
            panel.classList.remove('open');
        });
        
        // Vider la liste des panneaux ouverts
        this.$openedPanels = [];
        
        // Fermer le menu principal
        this.$mainHeaderNav?.classList.remove('submenu-is-open');

        // On enlève le trap focus
        FocusManager.untrapFocus();

        // On remet le focus sur l'élément qui a ouvert le menu
        if (this.lastFocusedElement) {
            this.lastFocusedElement.focus();
        }
    }

    /**
     * Gère le clic sur les boutons de retour de niveau 2 en mobile
     * 
     * @param {Event} event 
     * @return {void}
     */
    private handleMobileReturnLvl2Click(event: Event): void {
        event.preventDefault();
        const button = event.currentTarget as HTMLElement;
        const tabContent = button.closest('.menu-item-submenu-tabs-content-item');

        if (tabContent) {
            tabContent.classList.remove('open');
            
            // Trouver et fermer le bouton tab opener ouvert
            const openTabOpener = this.$el?.querySelector('.menu-item-submenu-tab-opener.open[aria-expanded="true"]') as HTMLElement;
            if (openTabOpener) {
                openTabOpener.classList.remove('open');
                openTabOpener.setAttribute('aria-expanded', 'false');
            }
            
            // On enlève le trap focus du niveau 2
            FocusManager.untrapFocus();
            // On remet le focus sur l'onglet actif
            const activeTab = this.$el?.querySelector('.menu-item-submenu-tab-opener.active');
            if (activeTab) {
                (activeTab as HTMLElement).focus();
                // On réactive le trap focus sur le niveau 1
                const submenu = activeTab.closest('.menu-item-submenu');
                if (submenu) {
                    FocusManager.trapFocus([submenu as HTMLElement]);
                }
            }
        }
    }

    /**
     * Gère le comportement sticky du js-wrapper-summary
     *
     * @return {void}
     */
    handleSecondaryStickyBehavior() {
        if (!this.$wrapperSummary) return;

        const currentScrollY = window.pageYOffset;
        const rect = this.$wrapperSummary.getBoundingClientRect();
        const isSticking = rect.top <= 32;
        const isScrollingUp = currentScrollY < this.lastScrollY;

        if (isSticking && !isScrollingUp) {
            this.$el?.classList.add('has-nav-secondary-sticky-active');
        } else {
            this.$el?.classList.remove('has-nav-secondary-sticky-active');
        }

        this.lastScrollY = currentScrollY;
    }
}
